// Librairies
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { map, Observable, of } from 'rxjs';

// Services
import { CompanyService } from 'src/app/services/api/companies/company.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';

// Interfaces
import { CompanyDetail, defaultTenant, NamedRef, Tenant } from 'src/app/interfaces/api/company';

// Components
import { localSearch, MemberOption, MemberSearchComponent } from 'src/app/components/shared/member-search/member-search.component';
import { ButtonComponent } from 'ui-library';

export interface TenantDialogData {
  company: CompanyDetail;
  tenant?: Tenant<NamedRef>;
}

export const TENANT_ICONS = [
  'apartment', 'business', 'domain', 'location_city', 'factory', 'warehouse', 'store', 'home_work',
  'construction', 'engineering', 'handyman', 'architecture', 'local_shipping', 'train', 'bolt', 'solar_power',
  'water_drop', 'eco', 'park', 'terrain', 'map', 'explore', 'flag', 'star',
  'shield', 'health_and_safety', 'groups', 'science', 'rocket_launch', 'insights'
];

export const TENANT_COLORS = [
  '#1E88E5', '#3949AB', '#8E24AA', '#D81B60', '#E53935', '#F4511E',
  '#FB8C00', '#F9A825', '#43A047', '#00897B', '#00ACC1', '#546E7A'
];

// API error field → message key.
const FIELD_ERRORS: Record<string, string> = {
  name: 'companies.errors.tenant_name_taken',
  projects: 'companies.errors.invalid_projects',
};

@Component({
  selector: 'app-tenant-dialog',
  standalone: true,
  imports: [FormsModule, MatDialogModule, MatFormFieldModule, MatIconModule, MatInputModule, MatTooltipModule, TranslateModule, ButtonComponent, MemberSearchComponent],
  templateUrl: './tenant-dialog.component.html',
  styleUrl: './tenant-dialog.component.scss'
})

export class TenantDialogComponent {
  // Services
  private readonly companyService = inject(CompanyService);
  private readonly projectService = inject(ProjectService);
  private readonly dialogRef = inject(MatDialogRef<TenantDialogComponent, boolean>);
  readonly data: TenantDialogData = inject(MAT_DIALOG_DATA);

  // Variables
  readonly icons = TENANT_ICONS;
  readonly colors = TENANT_COLORS;
  name = this.data.tenant?.name ?? '';

  // Signals
  readonly icon = signal(this.data.tenant?.icon ?? defaultTenant.icon);
  readonly color = signal(this.data.tenant?.color ?? defaultTenant.color);
  readonly projects = signal<NamedRef[]>(this.data.tenant?.projects ?? []);
  readonly projectIds = computed(() => this.projects().map(project => project.id));
  readonly allSelected = computed(() => !this.data.company.allProjects && this.projects().length === this.data.company.projects.length);
  readonly saving = signal(false);
  readonly error = signal('');

  // CAD.42 picks among every project (light list loaded at the first search), the others among their own projects.
  readonly searchProjects = localSearch(() => {
    const projects: Observable<NamedRef[]> = this.data.company.allProjects
      ? this.projectService.searchAndCountProjects('', { light: true, archived: true }).pipe(map(({ results }) => results))
      : of(this.data.company.projects);
    return projects.pipe(map(list => list.map(project => ({ id: project.id, label: project.name }))));
  });

  add(option: MemberOption): void {
    this.projects.update(projects => [...projects, { id: option.id, name: option.label }]);
  }

  remove(projectId: string): void {
    this.projects.update(projects => projects.filter(project => project.id !== projectId));
  }

  selectAll(): void {
    this.projects.set([...this.data.company.projects]);
  }

  save(): void {
    const name = this.name.trim();
    if (!name) return this.error.set('companies.errors.name_required');

    const { company, tenant } = this.data;
    const body = { name, icon: this.icon(), color: this.color(), projects: this.projectIds() };
    const request = tenant ? this.companyService.updateTenant(company.id, { id: tenant.id, ...body }) : this.companyService.addTenant(company.id, body);

    this.saving.set(true);
    this.error.set('');
    request.subscribe({
      next: () => this.dialogRef.close(true),
      error: err => {
        this.saving.set(false);
        this.error.set(FIELD_ERRORS[err?.error?.field] ?? err?.error?.error ?? 'companies.errors.generic');
      }
    });
  }

  close(): void {
    this.dialogRef.close(false);
  }
}
